import { minBy } from 'es-toolkit';
import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import CropRotateIcon from '@mui/icons-material/CropRotate';
import EditIcon from '@mui/icons-material/Edit';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import SouthIcon from '@mui/icons-material/South';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import { Divider } from '@mui/material';

import { Component, Member, Splice } from '@paragon/contract-library';
import { distanceEquals, LineSegment2D, Point2D } from '@paragon/geometry-library';
import { hasCanUsePlatesPermission, hasTrussDesignPermission } from '@paragon/login-tools';
import { calculateMemberMidline } from '@paragon/three-shared';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { SelectedItemIdentifier } from '../../../../library/selection/types';
import { useAppMonitor } from '../../../../monitor';
import {
  adjustCut,
  makeWebVertical,
  reverseWeb,
  setMemberLineLocation,
  stackWeb,
  unstackChord,
  unstackWeb,
} from '../../../../store/actions/editMemberActions';
import {
  startDropTopChords,
  startInsertMemberStack,
  startInsertPlatePair,
  startInsertSplice,
} from '../../../../store/actions/insertActions';
import { lockIndividualMember } from '../../../../store/actions/lockOptionsActions';
import { handleStandardSceneItemEditPropertiesAction } from '../../../../store/actions/sceneInteractionActions';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { getAsyncRequestForActiveComponentDesign } from '../../../../store/selectors/getAsyncRequestForActiveComponentDesignSelector';
import { getFlatTopChordGuids } from '../../../../store/selectors/getFlatTopChordGuidsSelector';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { toggleMemberCuttingMode } from '../../../../store/slices/applicationSlice';
import { RightClickedItemPosition } from '../../../../store/slices/viewportSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { LineLocationSelect } from '../../../interface/subpanels/properties/LineLocationSelect';
import { EditMemberMenuReplacement } from '../../menu-replacements/EditMemberMenuReplacement';
import CascadingSceneContextMenuItem from '../common/CascadingSceneMenuItem';
import { useSceneMenuContext } from '../common/context';
import { SceneMenuContent } from '../common/SceneMenuContent';
import { SceneMenuFragment } from '../common/SceneMenuFragment';
import { SceneMenuItem } from '../common/SceneMenuItem';
import { DeleteMenuItem } from '../shared-menu-items/DeleteMenuItem';

export function MemberMenu(props: {
  guid: string;
  position: RightClickedItemPosition;
  matchingSelectedItems: SelectedItemIdentifier[];
}) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const menuContext = useSceneMenuContext();

  const canEditTrusses = useAppSelector(hasTrussDesignPermission);
  const canUsePlates = useAppSelector(hasCanUsePlatesPermission);
  const componentDesign = useAppSelector(getActiveComponentDesign);

  const isViewOnly = useAppSelector(propertiesShouldBeViewOnly);
  const asyncRequest = useAppSelector(getAsyncRequestForActiveComponentDesign);
  const isMemberUpdatePending = !!asyncRequest && asyncRequest.type !== 'updateFeatures';
  const disabled = isViewOnly || isMemberUpdatePending;

  const flatTopChordGuids = useAppSelector(getFlatTopChordGuids);

  if (componentDesign == null || !canEditTrusses) return null;

  const memberGuids = new Set(
    props.matchingSelectedItems
      .filter((item) => item.type === 'member')
      .map((item) => item.guid)
      .concat(props.guid),
  );

  const members = componentDesign.component.members.filter((member) => memberGuids.has(member.guid));
  if (members.length === 0) return null;

  const singleMember = members.length === 1 ? members[0] : null;

  const clickDetails = singleMember && {
    ...determineClickedEnd(componentDesign.component, singleMember, props.position),
    ...determineClickedSplice(componentDesign.component, singleMember, props.position),
  };

  const allLocked =
    componentDesign.membersLocked ||
    Array.from(memberGuids).every((guid) => componentDesign.lockedMembers.includes(guid));

  const userVisibleTermWithQuantity = members.length > 1 ? `${members.length} Members` : 'Member';

  const numFlatTopChords = [...memberGuids].filter((guid) => flatTopChordGuids.includes(guid)).length;

  const dropTopChordsButtonName = numFlatTopChords === 1 ? 'Drop Flat Top Chord' : 'Drop Flat Top Chords';
  const dropTopChordsButton =
    numFlatTopChords === 0 ? null : (
      <SceneMenuItem
        text={dropTopChordsButtonName}
        icon={<SouthIcon />}
        onClick={() => {
          dispatch(startDropTopChords());
          monitor.recordInteraction(`Member => ${dropTopChordsButtonName} menu item clicked`);
        }}
      />
    );

  return (
    <SceneMenuFragment>
      <EditMemberMenuReplacement members={members} />

      <SceneMenuContent>
        <SceneMenuItem
          text="Edit Lumber"
          icon={<EditIcon />}
          onClick={async (event) => {
            event.preventDefault();
            menuContext.activateReplacement('edit-lumber');
          }}
        />

        {singleMember && (
          <SceneMenuFragment>
            <SceneMenuItem
              text="Edit Other Properties"
              icon={<EditIcon />}
              onClick={async () => {
                await dispatch(handleStandardSceneItemEditPropertiesAction('member', singleMember));
                monitor.recordInteraction('Member => Edit Properties menu item clicked');
              }}
            />

            {singleMember.relativeGeometry && (
              <SceneMenuFragment>
                <Divider />
                <CascadingSceneContextMenuItem
                  icon={<FormatLineSpacingIcon sx={{ rotate: '90deg' }} />}
                  label="Geometry"
                >
                  <LineLocationSelect
                    disabled={disabled}
                    clickMenu
                    relativeGeometry={singleMember.relativeGeometry}
                    submit={async (lineLocation) => {
                      await dispatch(setMemberLineLocation(componentDesign!.guid, singleMember, lineLocation));
                      monitor.recordInteraction('Member => Geometry sub-menu item clicked');
                    }}
                  />
                </CascadingSceneContextMenuItem>

                {(singleMember.memberType === 'TopChord' || singleMember.memberType === 'BottomChord') && (
                  <SceneMenuFragment>
                    <Divider />

                    {dropTopChordsButton}

                    <SceneMenuItem
                      text="Stack Chord"
                      icon={<PlaylistAddIcon />}
                      onClick={() => {
                        dispatch(startInsertMemberStack(props.guid));
                        monitor.recordInteraction('Member => Stack Chord menu item clicked');
                      }}
                    />

                    <SceneMenuItem
                      text="Unstack Chord"
                      icon={<PlaylistRemoveIcon />}
                      onClick={() => {
                        dispatch(unstackChord());
                        monitor.recordInteraction('Member => Unstack Chord menu item clicked');
                      }}
                    />
                  </SceneMenuFragment>
                )}
              </SceneMenuFragment>
            )}

            {singleMember.memberType === 'Web' && (
              <SceneMenuFragment>
                <Divider />
                <SceneMenuItem
                  text="Stack Web"
                  icon={<PlaylistAddIcon />}
                  onClick={() => {
                    dispatch(stackWeb(props.guid));
                    monitor.recordInteraction('Member => Stack Web menu item clicked');
                  }}
                />

                <SceneMenuItem
                  text="Unstack Web"
                  icon={<PlaylistRemoveIcon />}
                  onClick={() => {
                    dispatch(unstackWeb(props.guid));
                    monitor.recordInteraction('Member => Unstack Web menu item clicked');
                  }}
                />

                <SceneMenuItem
                  text="Reverse Web"
                  icon={<SwapHorizIcon />}
                  onClick={() => {
                    dispatch(reverseWeb(props.guid));
                    monitor.recordInteraction('Member => Reverse Web menu item clicked');
                  }}
                />
              </SceneMenuFragment>
            )}

            <Divider />

            <SceneMenuItem
              text="Cut Member (Shift)"
              icon={<ContentCutIcon />}
              onClick={() => {
                dispatch(toggleMemberCuttingMode('sticky'));
                monitor.recordInteraction('Member => Cut Member menu item clicked');
              }}
            />

            {singleMember.memberType === 'Web' && clickDetails?.atEnd && (
              <SceneMenuFragment>
                <CascadingSceneContextMenuItem
                  icon={<CropRotateIcon />}
                  label={`Adjust ${toHumanReadableEnd(clickDetails.atEnd)} Cuts`}
                >
                  <SceneMenuItem
                    text={`Recut ${toHumanReadableEnd(clickDetails.atEnd)} Clockwise`}
                    icon={
                      <RotateRightIcon
                        sx={{ transform: clickDetails.atEnd === 'upper' ? 'rotate(90deg)' : 'rotate(-90deg)' }}
                      />
                    }
                    onClick={() => {
                      dispatch(adjustCut(props.guid, clickDetails.atEnd === 'lower', true));
                      monitor.recordInteraction('Member => Recut Web Clockwise menu item clicked');
                    }}
                  />

                  <SceneMenuItem
                    text={`Recut ${toHumanReadableEnd(clickDetails.atEnd)} Counterclockwise`}
                    icon={
                      <RotateLeftIcon
                        sx={{ transform: clickDetails.atEnd === 'upper' ? 'rotate(-90deg)' : 'rotate(90deg)' }}
                      />
                    }
                    onClick={() => {
                      dispatch(adjustCut(props.guid, clickDetails.atEnd === 'lower', false));
                      monitor.recordInteraction('Member => Recut Web Counterclockwise menu item clicked');
                    }}
                  />
                </CascadingSceneContextMenuItem>

                <SceneMenuItem
                  text={`Move ${toHumanReadableEnd(clickDetails.atEnd)} to Make Vertical`}
                  icon={clickDetails.atEnd === 'lower' ? <VerticalAlignBottomIcon /> : <VerticalAlignTopIcon />}
                  onClick={() => {
                    dispatch(makeWebVertical(props.guid, clickDetails.atEnd === 'lower'));
                    monitor.recordInteraction('Member => Make Web Vertical menu item clicked');
                  }}
                />
              </SceneMenuFragment>
            )}

            <Divider />

            {clickDetails &&
              (singleMember.memberType === 'TopChord' || singleMember.memberType === 'BottomChord') &&
              !clickDetails.atSplice && (
                <SceneMenuItem
                  text="Add Splice"
                  icon={<AddIcon />}
                  onClick={() => {
                    dispatch(startInsertSplice());
                    monitor.recordInteraction('Member => Add Splice menu item clicked');
                  }}
                />
              )}

            <SceneMenuItem
              text="Add Plate Pair"
              icon={<AddIcon />}
              disabled={!canUsePlates}
              onClick={() => {
                dispatch(startInsertPlatePair());
                monitor.recordInteraction('Member => Add Plate Pair menu item clicked');
              }}
            />
          </SceneMenuFragment>
        )}

        {!singleMember && dropTopChordsButton}

        <Divider />

        <SceneMenuItem
          text={`${allLocked ? 'Unlock' : 'Lock'} ${userVisibleTermWithQuantity}`}
          icon={allLocked ? <LockOpenIcon /> : <LockIcon />}
          onClick={() => {
            dispatch(lockIndividualMember(!allLocked, ...memberGuids));
            monitor.recordInteraction('Member => Lock Member menu item clicked');
          }}
        />

        {singleMember && clickDetails?.atSplice ? (
          <DeleteMenuItem type="splice" item={clickDetails.atSplice} />
        ) : (
          <DeleteMenuItem
            type="member"
            item={members}
            userVisibleTerm={userVisibleTermWithQuantity}
            monitorText="Member => Delete Members menu item clicked"
          />
        )}
      </SceneMenuContent>
    </SceneMenuFragment>
  );
}

function determineClickedEnd(
  component: Component,
  member: Member,
  position: RightClickedItemPosition,
): { atEnd: 'upper' | 'lower' } | null {
  // If the click is outside the scene, we will be unable to determine if an end was clicked.
  if (position.outsideScene) return null;

  const midline3D = calculateMemberMidline(member, component);
  if (midline3D == null) return null;

  const midline = LineSegment2D.fromSegmentObject(midline3D);
  const clickPosition = Point2D.fromObject(position.worldPosition);

  let lowerEnd;
  let upperEnd;

  if (distanceEquals(midline.basePoint.y, midline.endPoint.y)) {
    return null;
  }

  if (midline.basePoint.y < midline.endPoint.y) {
    lowerEnd = midline.basePoint;
    upperEnd = midline.endPoint;
  } else {
    lowerEnd = midline.endPoint;
    upperEnd = midline.basePoint;
  }

  if (lowerEnd && clickPosition.distanceTo(lowerEnd) < clickPosition.distanceTo(midline.midpoint)) {
    return { atEnd: 'lower' };
  }

  if (upperEnd && clickPosition.distanceTo(upperEnd) < clickPosition.distanceTo(midline.midpoint)) {
    return { atEnd: 'upper' };
  }

  return null;
}

function toHumanReadableEnd(end: 'upper' | 'lower') {
  switch (end) {
    case 'upper':
      return 'Upper End';

    case 'lower':
      return 'Lower End';

    default:
      assertExhaustiveSwitch(end);
  }
}

const SPLICE_CLICK_RADIUS = 6; // TODO: anti-zoom?

function determineClickedSplice(
  component: Component,
  member: Member,
  position: RightClickedItemPosition,
): { atSplice: Splice } | null {
  // If the click is outside the scene, we will be unable to determine if a splice was clicked.
  if (position.outsideScene) return null;

  if (component.chordSplices == null) return null;
  if (member.memberType !== 'TopChord' && member.memberType !== 'BottomChord') return null;

  const clickPosition2D = Point2D.fromObject(position.worldPosition);

  const relevantSplices = component.chordSplices
    .filter((splice) => splice.neighbor1Guid === member.guid || splice.neighbor2Guid === member.guid)
    .map((splice) => {
      const splicePosition = new Point2D(splice.centerPointX, splice.centerPointY);
      const distanceFromClick = clickPosition2D.distanceTo(splicePosition);

      return { splice, distanceFromClick };
    })
    .filter(({ distanceFromClick }) => distanceFromClick < SPLICE_CLICK_RADIUS);

  const closestRelevantSplice = minBy(relevantSplices, (splice) => splice.distanceFromClick);
  if (closestRelevantSplice) {
    return { atSplice: closestRelevantSplice.splice };
  }

  return null;
}
